import React, { useState } from 'react';
import { Box, Button, Typography, Paper, Alert } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { achievementTriggers } from '../../utils/achievementTriggers';

const AchievementTestPanel = () => {
  const { user } = useAuth();
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addResult = (test, success, message) => {
    setTestResults(prev => [...prev, { test, success, message, timestamp: new Date() }]);
  };

  const testMovieWatched = async () => {
    setLoading(true);
    try {
      const result = await achievementTriggers.markWatched(
        user.userId, 
        'test-movie-123', 
        { title: 'Test Movie for Achievement' }
      );
      addResult('Movie Watched', true, `Triggered successfully. Unlocked: ${result?.length || 0} achievements`);
    } catch (error) {
      addResult('Movie Watched', false, error.message);
    }
    setLoading(false);
  };

  const testReviewSubmit = async () => {
    setLoading(true);
    try {
      const result = await achievementTriggers.submitReview(
        user.userId,
        'test-movie-456',
        4,
        150
      );
      addResult('Review Submit', true, `Triggered successfully. Unlocked: ${result?.length || 0} achievements`);
    } catch (error) {
      addResult('Review Submit', false, error.message);
    }
    setLoading(false);
  };

  const testFollowUser = async () => {
    setLoading(true);
    try {
      const result = await achievementTriggers.followUser(
        user.userId,
        'test-user-789'
      );
      addResult('Follow User', true, `Triggered successfully. Unlocked: ${result?.length || 0} achievements`);
    } catch (error) {
      addResult('Follow User', false, error.message);
    }
    setLoading(false);
  };

  const testDailyLogin = async () => {
    setLoading(true);
    try {
      const result = await achievementTriggers.dailyLogin(user.userId);
      addResult('Daily Login', true, `Triggered successfully. Unlocked: ${result?.length || 0} achievements`);
    } catch (error) {
      addResult('Daily Login', false, error.message);
    }
    setLoading(false);
  };

  if (!user) {
    return (
      <Alert severity="warning">
        Please login to test the achievement system
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Typography variant="h5" gutterBottom>
        Achievement System Test Panel
      </Typography>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        User ID: {user.userId}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Button 
          variant="contained" 
          onClick={testMovieWatched}
          disabled={loading}
        >
          Test Movie Watched
        </Button>
        
        <Button 
          variant="contained" 
          onClick={testReviewSubmit}
          disabled={loading}
        >
          Test Review Submit
        </Button>
        
        <Button 
          variant="contained" 
          onClick={testFollowUser}
          disabled={loading}
        >
          Test Follow User
        </Button>
        
        <Button 
          variant="contained" 
          onClick={testDailyLogin}
          disabled={loading}
        >
          Test Daily Login
        </Button>
      </Box>

      <Typography variant="h6" gutterBottom>
        Test Results:
      </Typography>
      
      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
        {testResults.map((result, index) => (
          <Alert 
            key={index}
            severity={result.success ? 'success' : 'error'}
            sx={{ mb: 1 }}
          >
            <strong>{result.test}:</strong> {result.message}
            <br />
            <small>{result.timestamp.toLocaleTimeString()}</small>
          </Alert>
        ))}
      </Box>
      
      {testResults.length === 0 && (
        <Typography color="text.secondary">
          No tests run yet. Click the buttons above to test achievement triggers.
        </Typography>
      )}
    </Paper>
  );
};

export default AchievementTestPanel;